import { Component, Input } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { GetUserRelationshipsResponse, NotificationType, UserRelationship, UserRelationshipType } from "@apiserver-rest/ponder-data-client-base";
import { AuthService } from "@ponder-core/auth/auth.service";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { Subscription, take } from "rxjs";
import { UserFriendComponent } from "./user-friend.component";
import { UserFriendAddComponent } from "./user-friend-add.component";
import { AuthSession } from "@ponder-core/auth/auth-session";
import { NotificationService } from "@ponder-core/notifications/notification.service";
import { UnauthorizedComponent } from "@ponder-shared/components/unauthorized.component";

@Component({
    imports: [LoadingComponent, UserFriendComponent, UserFriendAddComponent, LoadingComponent, UnauthorizedComponent],
    templateUrl: "./user-friends.component.html"
})
export class UserFriendsComponent {
    @Input() public username!: string;

    public isOwnFriends: boolean = false;
    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;

    public pending: UserRelationship[] = [];
    public friends: UserRelationship[] = [];

    public authSession?: AuthSession;

    private friendInviteSentSub!: Subscription;
    private friendAcceptedSub!: Subscription;
    private friendDeletedSub!: Subscription;

    public constructor( private ponderDataClient: PonderDataClient,  private auth: AuthService,  private pageTitle: Title,  private events: EventBroker, private notifications: NotificationService) {
        this.friendInviteSentSub = this.events.profile.friendInviteSentEvent.subscribe({
            next: (friendship: UserRelationship) => {
                this.onFriendInviteSent(friendship);
            }
        });

        this.friendAcceptedSub = this.events.profile.friendInviteAcceptedEvent.subscribe({
            next: (friendship: UserRelationship) => {
                this.onFriendAccepted(friendship);
            }
        });

        this.friendDeletedSub = this.events.profile.friendDeletedEvent.subscribe({
            next: (friendship: UserRelationship) => {
                this.onFriendDeleted(friendship);
            }
        });
    }

    public ngOnDestroy(): void {
        this.friendInviteSentSub?.unsubscribe();
        this.friendAcceptedSub?.unsubscribe();
        this.friendDeletedSub?.unsubscribe();
    }

    public ngOnInit(): void {
        this.pageTitle.setTitle(`Ponder > ${this.username} > Friends`);

        this.events.ui.toggleSortFilterBarEvent.emit(false);
        this.events.ui.navbarUpdateEvent.emit();

        this.auth.getSessionAsync().then((authSession?: AuthSession) => {
            this.authSession = authSession;
            
            if (authSession && (authSession.username == this.username))
                {
                    this.isOwnFriends = true;
                    this.ponderDataClient
                        .userRelationships()
                        .pipe(take(1))
                        .subscribe({
                            next: (response: GetUserRelationshipsResponse) => {
                                if (response.success) {
                                    response.relationships?.forEach(r => {
                                        switch (r.type) {
                                            case UserRelationshipType.Friends:
                                                this.friends.push(r);
                                                break;
        
                                            case UserRelationshipType.Pending:
                                                this.pending.push(r);
                                                break;
        
                                            default:
                                                break;
                                        }
                                    });
        
                                    this.sortAllRelationships();
                                    this.loadingSuccess = true;
                                    
                                    this.notifications.clearUserNotificationsOfType(NotificationType.FriendPending).then();
                                }
                            }
                        })
                        .add(() => { this.isLoading = false; });
                } else {
                    this.isOwnFriends = false;
                    this.isLoading = false;
                }
        });
    }

    // -------------------------------------------------------------------------------------
    // Events
    // -------------------------------------------------------------------------------------

    private onFriendInviteSent(friendship: UserRelationship): void {
        for (let i = 0; i < this.pending.length; ++i) {
            if (this.pending[i].otherUserId == friendship.otherUserId) {
                return;
            }
        }

        for (let i = 0; i < this.friends.length; ++i) {
            if (this.friends[i].otherUserId == friendship.otherUserId) {
                return;
            }
        }

        this.pending.push(friendship);
        
        this.sortRelationships(this.pending);
    }

    private onFriendAccepted(friendship: UserRelationship): void {
        friendship.type = UserRelationshipType.Friends;

        var friendIndex = this.getIndex(friendship, this.friends);
        var pendingIndex = this.getIndex(friendship, this.pending);

        if (pendingIndex >= 0) {
            this.pending.splice(pendingIndex, 1);
        }

        if (friendIndex < 0) {
            this.friends.push(friendship);
        }

        this.sortRelationships(this.friends);
    }

    private onFriendDeleted(friendship: UserRelationship): void {
        friendship.type = UserRelationshipType.None;

        var friendIndex = this.getIndex(friendship, this.friends);
        var pendingIndex = this.getIndex(friendship, this.pending);

        if (friendIndex >= 0) {
            this.friends.splice(friendIndex, 1);
        }

        if (pendingIndex >= 0) {
            this.pending.splice(pendingIndex, 1);
        }
    }
    
    // -------------------------------------------------------------------------------------
    // Misc
    // -------------------------------------------------------------------------------------

    private getIndex(relationship: UserRelationship, relationshipArr: UserRelationship[]): number {
        for (let i = 0; i < relationshipArr.length; ++i) {
            if (relationshipArr[i].otherUserId == relationship.otherUserId) {
                return i;
            }
        }
        
        return -1;
    }

    private sortAllRelationships(): void {
        this.sortRelationships(this.friends);
        this.sortRelationships(this.pending);
    }

    private sortRelationships(relationships: UserRelationship[]): void {
        relationships.sort((a, b) => {
            var aName = a.otherDisplayName ?? a.otherUsername!;
            var bName = b.otherDisplayName ?? b.otherUsername!;

            return aName.localeCompare(bName);
        });
    }


}